<template>
  <el-container class="admin-page">
    <el-header class="admin-page__title">
      意向用户管理
    </el-header>

    <el-main>
      <div class="admin-page__toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="输入姓名、公司、邮箱或电话进行搜索"
          :style="{ width: isMobile ? '100%' : '300px' }"
          clearable
          @input="handleSearch"
        >
          <template #prefix>
            <i class="el-icon-search"></i>
          </template>
        </el-input>
      </div>

      <!-- PC端表格视图 -->
      <el-table 
        v-if="!isMobile"
        :data="interestedUsers" 
        style="width: 100%"
        stripe
        v-loading="loading"
      >
        <el-table-column prop="name" label="姓名" width="120" />
        <el-table-column prop="company" label="公司名称" width="180" />
        <el-table-column prop="phone" label="电话" width="150" />
        <el-table-column prop="email" label="邮箱" width="200" />
        <el-table-column prop="message" label="留言" min-width="160" />
        <el-table-column prop="created_at" label="提交时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 'processed' ? 'success' : 'warning'">
              {{ row.status === 'processed' ? '已处理' : '未处理' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template #default="{ row }">
            <el-button 
              @click="markAsProcessed(row)" 
              type="text" 
              size="small"
              :disabled="row.status === 'processed'"
            >
              标记为已处理
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 移动端卡片视图 -->
      <div v-else class="mobile-cards" v-loading="loading">
        <el-card v-for="user in interestedUsers" :key="user.id" class="mobile-card">
          <div class="card-item">
            <span class="label">姓名：</span>
            <span class="value">{{ user.name }}</span>
          </div>
          <div class="card-item">
            <span class="label">公司：</span>
            <span class="value">{{ user.company }}</span>
          </div>
          <div class="card-item">
            <span class="label">电话：</span>
            <span class="value">{{ user.phone }}</span>
          </div>
          <div class="card-item">
            <span class="label">邮箱：</span>
            <span class="value">{{ user.email }}</span>
          </div>
          <div class="card-item">
            <span class="label">留言：</span>
            <span class="value">{{ user.message }}</span>
          </div>
          <div class="card-item">
            <span class="label">提交时间：</span>
            <span class="value">{{ formatDate(user.created_at) }}</span>
          </div>
          <div class="card-item">
            <span class="label">状态：</span>
            <el-tag :type="user.status === 'processed' ? 'success' : 'warning'">
              {{ user.status === 'processed' ? '已处理' : '未处理' }}
            </el-tag>
          </div>
          <div class="card-actions">
            <el-button 
              @click="markAsProcessed(user)" 
              type="primary" 
              size="small"
              :disabled="user.status === 'processed'"
            >
              标记为已处理
            </el-button>
          </div>
        </el-card>
      </div>

      <!-- 分页器 -->
      <el-pagination
        class="admin-page__pager"
        background
        :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
        :total="totalCount"
        :page-size="pageSize"
        :current-page="currentPage"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-main>
  </el-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { ElMessage } from 'element-plus';
import authApi from '@/js/connect';

const { width } = useWindowSize();
const isMobile = computed(() => width.value <= 768);

const searchQuery = ref('');
const interestedUsers = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const totalCount = ref(0);

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const fetchInterestedUsers = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      per_page: pageSize.value,
      search: searchQuery.value
    };
    const response = await authApi.getAllInterestedUsers(params);
    interestedUsers.value = response.data.users.map(user => ({
      ...user,
      status: user.status || 'pending'  // 如果 status 不存在，默认为 'pending'
    }));
    totalCount.value = response.data.total_count;
  } catch (error) {
    console.error("获取意向用户数据失败:", error);
    ElMessage.error('获取意向用户数据失败，请稍后重试');
  } finally {
    loading.value = false;
  }
};

const handleSizeChange = (val) => {
  pageSize.value = val;
  fetchInterestedUsers();
};

const handleCurrentChange = (val) => {
  currentPage.value = val;
  fetchInterestedUsers();
};

const handleSearch = () => {
  currentPage.value = 1;
  fetchInterestedUsers();
};

const markAsProcessed = async (user) => {
  try {
    await authApi.markInterestedUserAsProcessed(user.id);
    ElMessage.success('已标记为处理');
    fetchInterestedUsers();
  } catch (error) {
    console.error("标记失败:", error);
    ElMessage.error('标记失败，请稍后重试');
  }
};

onMounted(() => {
  fetchInterestedUsers();
});
</script>

<style scoped>
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mobile-card {
  margin-bottom: 10px;
  border-radius: var(--dca-radius);
  background-color: var(--dca-surface);
  border: 1px solid var(--dca-border);
  box-shadow: none;
}

.card-item {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid var(--dca-border);
}

.card-item:last-child {
  border-bottom: none;
}

.card-item .label {
  width: 80px;
  color: var(--dca-text-muted);
  font-size: 14px;
}

.card-item .value {
  flex: 1;
  color: var(--dca-text);
  font-size: 14px;
  word-break: break-all;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--dca-border);
}
</style>